import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { ArrowRight, Loader2 } from "lucide-react";
import { useLanguage } from "../i18n";
import { useSEO } from "../seo";
import { PAGE_CONTENT } from "../content-pages";
import { ARTICLES } from "../articles";

const EASE = [0.22, 1, 0.36, 1];
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Reveal({ children, delay = 0, className = "" }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.9, delay, ease: EASE }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

function Meta({ article, lang }) {
  return (
    <p className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs font-bold uppercase tracking-[0.15em] text-mute">
      <span className="rounded-full bg-navy px-3 py-1 text-[10px] text-sky">{article.category[lang]}</span>
      <span>{article.author}</span>
      <span aria-hidden>·</span>
      <span>{article.date[lang]}</span>
    </p>
  );
}

export default function GuidePage() {
  const { lang } = useLanguage();
  const g = PAGE_CONTENT[lang].guide;
  useSEO(
    lang === "en" ? "Journal · Guides and tips from Ogliastra | OGStay" : "Journal · Guide e consigli dall'Ogliastra | OGStay",
    lang === "en"
      ? "Beaches, villages, farm stays and owner tips: the OGStay journal about authentic Ogliastra and Sardinia."
      : "Spiagge, borghi, agriturismi e consigli per proprietari: il journal di OGStay sull'Ogliastra autentica e la Sardegna."
  );
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [featured, ...rest] = ARTICLES;

  const subscribe = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/leads`, { name: email.split("@")[0], email, kind: "newsletter", locale: lang });
      toast.success(g.newsletter.success);
      setEmail("");
    } catch {
      toast.error(g.newsletter.error);
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="guide-page" className="bg-warm">
      {/* Hero */}
      <section className="bg-navy pb-20 pt-40 text-white sm:pb-24 sm:pt-48">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <motion.p initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: EASE }} className="kicker text-sky">
            <span className="h-px w-10 bg-sky" />
            {g.kicker}
          </motion.p>
          <motion.h1
            data-testid="guide-hero-title"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.12, ease: EASE }}
            className="mt-6 max-w-3xl font-display text-4xl font-semibold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl"
          >
            {g.title}
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.24, ease: EASE }} className="mt-6 max-w-xl text-base leading-relaxed text-white/75 sm:text-lg">
            {g.sub}
          </motion.p>
        </div>
      </section>

      {/* Featured */}
      <section className="py-20 sm:py-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <Link
              to={`/guide/${featured.slug}`}
              data-testid="guide-featured-article"
              className="group grid items-center gap-10 rounded-[2.5rem] bg-sand p-8 ring-1 ring-navy/5 transition-shadow duration-500 hover:shadow-[0_40px_80px_-30px_rgba(15,43,58,0.3)] sm:p-12 lg:grid-cols-2"
            >
              <div className="overflow-hidden rounded-[1.75rem]">
                <img src={featured.image} alt={featured[lang].title} loading="lazy" className="aspect-[16/10] w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.05]" />
              </div>
              <div>
                <span className="kicker text-navy">
                  <span className="h-px w-8 bg-sky" />
                  {g.featured}
                </span>
                <h2 className="mt-5 font-display text-3xl font-semibold leading-tight tracking-tight text-navy sm:text-4xl">{featured[lang].title}</h2>
                <p className="mt-4 text-base leading-relaxed text-mute">{featured[lang].excerpt}</p>
                <Meta article={featured} lang={lang} />
                <span className="mt-7 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.15em] text-navy transition-colors group-hover:text-med">
                  {g.readMore}
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
                </span>
              </div>
            </Link>
          </Reveal>

          {/* Grid */}
          <div className="mt-14 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {rest.map((article, i) => (
              <Reveal key={article.slug} delay={i * 0.08} className="h-full">
                <Link
                  to={`/guide/${article.slug}`}
                  data-testid={`guide-article-${article.slug}`}
                  className="group flex h-full flex-col overflow-hidden rounded-[1.75rem] bg-white ring-1 ring-navy/10 transition-[transform,box-shadow] duration-500 hover:-translate-y-2 hover:shadow-[0_30px_60px_-25px_rgba(15,43,58,0.3)]"
                >
                  <div className="aspect-[4/3] overflow-hidden">
                    <img src={article.image} alt={article[lang].title} loading="lazy" className="h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.06]" />
                  </div>
                  <div className="flex flex-1 flex-col p-7">
                    <h3 className="font-display text-xl font-semibold leading-snug text-navy">{article[lang].title}</h3>
                    <p className="mt-3 flex-1 text-sm leading-relaxed text-mute">{article[lang].excerpt}</p>
                    <Meta article={article} lang={lang} />
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section data-testid="guide-newsletter" className="bg-navy py-20 text-white sm:py-24">
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-5 sm:px-8 lg:grid-cols-2">
          <Reveal>
            <h2 className="font-display text-3xl font-semibold leading-tight tracking-tight sm:text-4xl">{g.newsletter.title}</h2>
            <p className="mt-4 max-w-md text-base leading-relaxed text-white/65">{g.newsletter.sub}</p>
          </Reveal>
          <Reveal delay={0.12}>
            <form data-testid="newsletter-form" onSubmit={subscribe} className="flex flex-col gap-3 sm:flex-row">
              <input
                data-testid="newsletter-email-input"
                required
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={g.newsletter.emailPh}
                className="flex-1 rounded-full border border-white/20 bg-white/10 px-6 py-4 text-sm font-semibold text-white placeholder-white/50 backdrop-blur-sm transition-[border-color,box-shadow] duration-300 focus:border-sky focus:outline-none focus:ring-2 focus:ring-sky/30"
              />
              <button data-testid="newsletter-submit-button" type="submit" disabled={sending} className="btn-primary whitespace-nowrap disabled:opacity-60">
                {sending && <Loader2 className="h-4 w-4 animate-spin" />}
                {sending ? g.newsletter.sending : g.newsletter.submit}
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
